/**
 * 分域派生 —— 修复原型缺陷 **B1**（密钥每次重启重建）与 **B3**（DID 伪造 `user.xxxx`）。
 *
 * 口径（ARCH §8.3，全站唯一）：
 *  - seed → 三套种子：`HMAC-SHA512(key = seed, data = "MeshLink identity/sign" | "/enc" | "/peer")`，各取前 32B
 *  - address = `'0x' + sha256(ed25519 公钥).hex().slice(0, 40)`（小写）
 *  - did     = `'did:dpb:' + address`
 *  - PeerID  = Ed25519（由 peerSeed 派生），与 DID 同源不同密钥
 *
 * **同助记词必须确定性复现**（PRD §9.3 B1 的验收判据）。
 */

import {
  ed25519PublicKey,
  hashSha256,
  hmacSha512,
  base58Encode,
  x25519PublicKey,
} from './noble';
import { bytesToBase64, bytesToHex } from '@/lib/format';
import type { DerivedIdentity } from '@shared/types';

/** 三个派生域（改任一字符串都会导致已有身份不可恢复，禁止修改）。 */
export const DOMAIN = {
  sign: 'MeshLink identity/sign',
  enc: 'MeshLink identity/enc',
  peer: 'MeshLink identity/peer',
} as const;

/** 从 seed 派出一个域的 32 字节子种子。 */
export function deriveDomainSeed(seed: Uint8Array, domain: string): Uint8Array {
  const full = hmacSha512(seed, new TextEncoder().encode(domain));
  return full.slice(0, 32);
}

/** Ed25519 公钥 → 链上地址（`0x` + 40 hex）。 */
export function addressFromPublicKey(signPub: Uint8Array): `0x${string}` {
  const digest = bytesToHex(hashSha256(signPub));
  return `0x${digest.slice(0, 40)}` as `0x${string}`;
}

/** 地址 → DID。 */
export function didFromAddress(address: string): string {
  return `did:dpb:${address.toLowerCase()}`;
}

/**
 * PeerID 派生（libp2p 形态）：
 *   protobuf 公钥 = 0x08 0x01(KeyType=Ed25519) 0x12 0x20 ‖ pub(32B)
 *   identity multihash = 0x00 0x24(36) ‖ protobuf
 *   peerId = base58btc(multihash)  → 形如 `12D3KooW…`
 */
export function peerIdFromPublicKey(peerPub: Uint8Array): string {
  const protobuf = new Uint8Array(4 + peerPub.length);
  protobuf[0] = 0x08; // field 1 (KeyType), varint
  protobuf[1] = 0x01; // Ed25519
  protobuf[2] = 0x12; // field 2 (Data), length-delimited
  protobuf[3] = 0x20; // 32
  protobuf.set(peerPub, 4);

  const multihash = new Uint8Array(2 + protobuf.length);
  multihash[0] = 0x00; // identity
  multihash[1] = 0x24; // 36
  multihash.set(protobuf, 2);

  return base58Encode(multihash);
}

export interface DerivedKeys {
  signSeed: Uint8Array;
  encSeed: Uint8Array;
  peerSeed: Uint8Array;
  signPub: Uint8Array;
  encPub: Uint8Array;
  peerPub: Uint8Array;
  address: `0x${string}`;
  did: string;
  peerId: string;
}

/** seed → 完整密钥与身份标识（纯同步，确定性）。 */
export function deriveKeysFromSeed(seed: Uint8Array): DerivedKeys {
  const signSeed = deriveDomainSeed(seed, DOMAIN.sign);
  const encSeed = deriveDomainSeed(seed, DOMAIN.enc);
  const peerSeed = deriveDomainSeed(seed, DOMAIN.peer);

  const signPub = ed25519PublicKey(signSeed);
  const encPub = x25519PublicKey(encSeed);
  const peerPub = ed25519PublicKey(peerSeed);

  const address = addressFromPublicKey(signPub);
  const did = didFromAddress(address);
  const peerId = peerIdFromPublicKey(peerPub);

  return { signSeed, encSeed, peerSeed, signPub, encPub, peerPub, address, did, peerId };
}

/** 把派生结果转成可 seal 的 `DerivedIdentity`（密钥以 base64 承载，便于加密存储）。 */
export function toDerivedIdentity(keys: DerivedKeys): DerivedIdentity {
  return {
    did: keys.did,
    address: keys.address,
    peerId: keys.peerId,
    signPub: bytesToBase64(keys.signPub),
    encPub: bytesToBase64(keys.encPub),
    signSeed: bytesToBase64(keys.signSeed),
    encSeed: bytesToBase64(keys.encSeed),
    peerSeed: bytesToBase64(keys.peerSeed),
  };
}

/**
 * 解锁后由三件套种子重建完整身份（与创建时完全一致，确定性）。
 * 注意：这里**不再**走 HMAC 派生 —— 三个种子本身已是派生终点，只需重建公钥即可。
 */
export function rebuildKeys(signSeed: Uint8Array, encSeed: Uint8Array, peerSeed: Uint8Array): DerivedKeys {
  const signPub = ed25519PublicKey(signSeed);
  const encPub = x25519PublicKey(encSeed);
  const peerPub = ed25519PublicKey(peerSeed);
  const address = addressFromPublicKey(signPub);
  return {
    signSeed,
    encSeed,
    peerSeed,
    signPub,
    encPub,
    peerPub,
    address,
    did: didFromAddress(address),
    peerId: peerIdFromPublicKey(peerPub),
  };
}
